import {Component, OnInit, OnDestroy} from '@angular/core';
import {FormGroup, FormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import CustomerDeviceModel from '../../../services/customerDevice.model';
import {CustomerDeviceService} from '../../../services/customerDevice.service';
import SaleTypeModel from '../../../services/saleType.model';
import {SaleTypeService} from '../../../services/saleType.service';
import moment from 'moment';
import * as _ from 'lodash';
import DeviceMovementModel from '../../../services/deviceMovement.model';
import {DeviceMovementService} from '../../../services/deviceMovement.service';
import {ToastrService} from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

/**
 * @component MapDeviceComponent
 * @selector app-map-device
 * @description
 * The `MapDeviceComponent` is responsible for mapping customer devices (IoT devices) to specific business entities
 * within the Trading Community Admin Panel. It provides an interface to select unmapped devices, associate them
 * with a business, assign a sale type, and record the device movement history.
 *
 * ## Key Responsibilities:
 * 1. **Device Mapping**: Allows administrators to link a physical device ID to a business entity.
 * 2. **Data Retrieval**: Fetches available non-mapped devices, sale types, and existing mapped devices for the selected business.
 * 3. **Transaction Logging**: Records the 'Map' action in the device movement history for audit trails.
 * 4. **Validation**: Ensures required fields are present before submission.
 * 5. **State Management**: Manages loading states, form submission states, and double-click prevention.
 *
 * ## Features:
 * - **Form Handling**: Reactive form for device selection and sale type assignment.
 * - **Async Data Loading**: Uses `Promise.all` to load multiple dependencies (devices, sale types, business details) concurrently.
 * - **Device Movement Tracking**: Automatically logs a device movement record upon successful mapping.
 * - **Role-Based Logic**: Retrieves user role from localStorage for audit logging.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Retrieves `businessId` from route parameters.
 *    - Fetches non-mapped devices, sale types, business details, and already mapped devices.
 * 2. **Mapping (`addMapDevice`)**:
 *    - Validates form input.
 *    - Calls `CustomerDeviceService.mapDevice` to link the device.
 *    - On success, calls `DeviceMovementService.deassignFun` (used for generic movement logging here) to record the 'Map' action.
 *    - Refreshes the list of devices.
 * 3. **Cancellation (`cancel`)**: Resets the form to its initial state.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: Used to track who performed the mapping action.
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-map-device',
  templateUrl: './map-device.component.html',
  styleUrls: ['./map-device.component.scss']
})
export class MapDeviceComponent implements OnInit, OnDestroy {
  /**
   * @type {Array<BusinessEntity>}
   * @description List of business entities available for mapping.
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * @type {BusinessEntity}
   * @description The currently selected business entity for which devices are being mapped.
   */
  public businessEntity: BusinessEntity;

  /**
   * @type {Array<BusinessEntity>}
   * @description List of all business entities in the system.
   */
  public allBusinesses: Array<BusinessEntity>;

  /**
   * @type {Array<CustomerDeviceModel>}
   * @description List of customer devices already mapped to the selected business.
   */
  public customerDevices: Array<CustomerDeviceModel>;

  /**
   * @type {CustomerDeviceModel}
   * @description Model for the device currently being mapped.
   */
  public customerDevice: CustomerDeviceModel;

  /**
   * @type {CustomerDeviceModel}
   * @description List of devices that are not currently mapped to any business.
   */
  public nonMappedDevice: CustomerDeviceModel;

  /**
   * @type {Array<SaleTypeModel>}
   * @description List of available sale types (e.g., Lease, Sale).
   */
  public saleTypes: Array<SaleTypeModel>;

  /**
   * @type {SaleTypeModel}
   * @description Selected sale type for the mapping.
   */
  public saleType: SaleTypeModel;

  /**
   * @type {DeviceMovementModel}
   * @description Model to track the movement/history of the device mapping action.
   */
  public deviceMovement: DeviceMovementModel;

  /**
   * @type {FormGroup}
   * @description Reactive form group for adding a map device.
   */
  public addMapDeviceForm: FormGroup;

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if the form has been submitted.
   */
  public submitted = false;

  /**
   * @type {string}
   * @description Business ID retrieved from route parameters.
   */
  public paramsbusinessId: string;

  /**
   * @type {boolean}
   * @default false
   * @description Flag to control the page loading spinner.
   */
  public pgLoad = false;

  /**
   * @type {string}
   * @description Name of the business entity.
   */
  public businessName: string;

  /**
   * @type {string}
   * @description User role of the logged-in person (e.g., Admin).
   */
  public loginPersonType: string;

  /**
   * @type {CustomerDeviceModel}
   * @description The result of the mapping operation returned from the API.
   */
  public mappedCustomerDevice: CustomerDeviceModel;

  /**
   * @type {boolean}
   * @description Flag to prevent double-clicking the submit button.
   */
  public mapDeviceDoubleClick: boolean;

  /**
   * @constructor
   * @description Initializes the component, services, and the reactive form.
   * @param {ToastrService} toasterService - Service for displaying notifications.
   * @param {SaleTypeService} saleTypeService - Service for fetching sale types.
   * @param {DeviceMovementService} deviceMovementService - Service for tracking device movements.
   * @param {CustomerDeviceService} customerDeviceService - Service for managing customer devices.
   * @param {BusinessEntityService} businessEntityService - Service for managing business entities.
   * @param {ActivatedRoute} route - Service for accessing route parameters.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking previous routes.
   */
  constructor(private toasterService: ToastrService, public saleTypeService: SaleTypeService, public deviceMovementService: DeviceMovementService, public customerDeviceService: CustomerDeviceService, public businessEntityService: BusinessEntityService, public route: ActivatedRoute, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.customerDevice = new CustomerDeviceModel();
    this.deviceMovement = new DeviceMovementModel();
    /*validation*/
    this.addMapDeviceForm = this.formBuilder.group({
      device_id: ['', Validators.required],
      saleTypeId: [null],
      pairedDeviceId: [null]
    });
    this.addMapDeviceForm.get('businessName')?.disable();
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description Initializes the component by fetching necessary data.
   * - Sets previous route.
   * - Retrieves login person type and business ID from route params.
   * - Loads non-mapped devices, sale types, business details, and existing mapped devices concurrently.
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    this.pgLoad = false;
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.mapDeviceDoubleClick = false;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      let promiseResults = new Array();
      await Promise.all([
        await this.customerDeviceService.getAllNonMapped(),
        await this.saleTypeService.getAllSaleTypes(),
        await this.businessEntityService.getOneByID(localStorage.loginBusinessID),
        await this.customerDeviceService.getAlladdMapDeviceWithFarm(localStorage.loginBusinessID),
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        this.toasterService.error(reject);
      });
      this.nonMappedDevice = promiseResults[0];
      this.saleTypes = promiseResults[1];
      this.businessEntity = promiseResults[2];
      this.customerDevices = promiseResults[3];
      this.pgLoad = true;
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description Cleans up resources by cancelling ongoing HTTP requests.
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.customerDeviceService.cancelOnGoingHttpRequests();
    this.saleTypeService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
  }

  /**
   * @method f
   * @description Convenience getter for easy access to form fields in the template.
   * @returns {AbstractControl} The form controls.
   */
  public get f() {
    return this.addMapDeviceForm.controls;
  }

  /**
   * @method addMapDevice
   * @async
   * @description Handles the submission of the map device form.
   * 1. Validates the form.
   * 2. Maps the selected device to the business using `CustomerDeviceService`.
   * 3. Logs the mapping action using `DeviceMovementService`.
   * 4. Refreshes the list of devices and non-mapped devices.
   * 5. Displays success or error messages.
   *
   * @side-effect
   * - Updates `customerDevices` and `nonMappedDevice` lists.
   * - Resets form submission state.
   * - Shows toast notifications.
   * @returns {Promise<void>}
   */
  public async addMapDevice() {
    try {
      this.submitted = true;
      // stop here if form is invalid
      if (this.addMapDeviceForm.invalid) {
        return;
      }
      this.mapDeviceDoubleClick = true;
      this.customerDevice.device_id = [this.customerDevice.device_id];
      this.customerDevice.business_id = localStorage.loginBusinessID;
      if (this.businessEntity.businessType[0].name == 'Feed Provider' || this.businessEntity.businessType[0].name == 'Seed Provider') {
        this.customerDevice.businessTypeID = this.businessEntity.businessType[0].businessTypeID;
      }
      this.mappedCustomerDevice = await this.customerDeviceService.mapDevice(this.customerDevice);
      this.toasterService.success('Map device added successfully', '', {
        timeOut: 3000
      });
      this.submitted = false;
      this.mapDeviceDoubleClick = false;
      this.customerDevice = new CustomerDeviceModel();
      let promiseResults = new Array();
      await Promise.all([
        await this.customerDeviceService.getAlladdMapDeviceWithFarm(localStorage.loginBusinessID),
        await this.customerDeviceService.getAllNonMapped(),
      ]).then((resolve) => {
        promiseResults = resolve;
      }).catch((reject) => {
        this.toasterService.error(reject);
      });
      this.customerDevices = promiseResults[0];
      this.nonMappedDevice = promiseResults[1];
      this.submitted = false;
      /*call add device movement service*/
      /*find business name by selected businessid*/
      const selectedBusinessObj = _.filter(this.allBusinesses, (o) => {
        return o.id == this.customerDevice.business_id;
      });
      if (selectedBusinessObj.length > 0) {
        this.businessName = selectedBusinessObj[0].businessName;
      }
      /*find business name by selected businessid*/
      this.deviceMovement.from_loc = 2;
      this.deviceMovement.to_loc = 1;
      this.deviceMovement.business_id = localStorage.loginBusinessID;
      this.deviceMovement.to = this.businessEntity.businessName;
      this.deviceMovement.to_id = localStorage.loginBusinessID;
      this.deviceMovement.device_id = '' + this.mappedCustomerDevice.device_id;
      this.deviceMovement.action_name = 'Map';
      this.deviceMovement.date = moment(new Date()).utc().format('YYYY-MM-DD');

      let dt = new Date();
      let dtime = dt.getHours() + ':' + dt.getMinutes() + ':' + dt.getSeconds();

      this.deviceMovement.time = dtime;
      this.deviceMovement.created_by = this.loginPersonType;
      this.deviceMovement.updated_by = this.loginPersonType;
      this.deviceMovement.provider_id = localStorage.loginBusinessID;

      // FIX
      await this.deviceMovementService.deassignFun(this.deviceMovement);

      // reset AFTER API calls
      this.customerDevice = new CustomerDeviceModel();
      this.addMapDeviceForm.markAsPristine(); // optional - clears dirty flags
      this.addMapDeviceForm.markAsUntouched();
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
      this.mapDeviceDoubleClick = false;
    }
  }

  /**
   * @method cancel
   * @description Resets the form and component state to initial values.
   * Clears validation errors and resets form controls.
   * @returns {void}
   */
  public cancel(): void {
    this.submitted = false;
    this.mapDeviceDoubleClick = false;
    this.customerDevice = new CustomerDeviceModel();

    this.addMapDeviceForm.reset({
      businessName: this.businessEntity?.businessName || null,
      device_id: null,
      saleTypeId: null,
      pairedDeviceId: null
    });

    this.addMapDeviceForm.get('businessName')?.disable();

    // 🔥 Critical: remove touched state so red border won't show
    Object.values(this.addMapDeviceForm.controls).forEach(control => {
      control.markAsUntouched();
      control.markAsPristine();
      control.updateValueAndValidity();
    });
  }



}
